import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { TVDFormField } from '../form-field';
import {
  DateAdapter,
  DecimalAdapter,
  type InputAdapter,
  IntegerAdapter,
  TextAdapter,
} from './adapter';

const adapters = {
  text: TextAdapter,
  decimal: DecimalAdapter,
  integer: IntegerAdapter,
  date: DateAdapter,
};
export type AdapterType = keyof typeof adapters;
@Component({
  selector:
    'input[tvdInput]:not([type="checkbox"]):not([type="radio"]):not([type="number"]), textarea[tvdInput]',
  host: {
    '(input)': 'onInput()',
    '(blur)': 'onBlur()',
    '(focus)': 'onFocus()',
    '(keydown)': 'onKeydown($any($event))',
    '[disabled]': 'disabled()',
    '[attr.tabindex]': 'disabled() ? -1 : 0',
    '[value]': 'displayValue()',
    class: 'zyn-input-base w-full',
  },
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  exportAs: 'tvdInput',
})
export class Input<T> implements FormValueControl<T | null> {
  readonly value = model<T | null>(null);
  readonly disabled = input(false);
  readonly touched = model(false);
  /** Tells the bound field it has been touched (signal-forms `touch` output). */
  readonly touch = output<void>();
  /** Drives the `TVDFormField` asterisk; bound from the field schema by `[formField]`. */
  readonly required = input(false);

  adapterType = input<InputAdapter<T>, AdapterType | InputAdapter<T>>(
    new adapters.text() as InputAdapter<T>,
    {
      transform: (value: AdapterType | InputAdapter<T>) => {
        if (typeof value === 'string') {
          return new adapters[value]() as InputAdapter<T>;
        }
        return value;
      },
    },
  );
  readonly #formField = inject(TVDFormField, { optional: true });
  readonly #element = inject(ElementRef).nativeElement as
    | HTMLInputElement
    | HTMLTextAreaElement;
  readonly #isFocused = signal(false);
  readonly #rawInput = signal('');

  constructor() {
    this.#formField?.control.set(this);
    // Restore the focused-state resync dropped with the CVA writeValue: when the value is
    // written externally while focused, refresh the raw text from the formatted value so the
    // display tracks the model. Skip when the formatted value still echoes the current raw
    // text — that change came from the user's own keystrokes, so leave their input alone.
    effect(() => {
      const formatted = this.formattedValue() ?? '';
      untracked(() => {
        if (!this.#isFocused()) return;
        const adapter = this.adapterType();
        if (adapter.format(adapter.parse(this.#rawInput())) !== formatted) {
          this.#rawInput.set(formatted);
        }
      });
    });
  }

  readonly formattedValue = computed(() =>
    this.adapterType()?.format(this.value()),
  );
  readonly displayValue = computed(() =>
    this.#isFocused() ? this.#rawInput() : this.formattedValue(),
  );
  onBlur() {
    this.#isFocused.set(false);
    if (this.value()) {
      this.#setValue(this.value() as unknown as T);
    }
    this.touched.set(true);
    this.touch.emit();
  }
  onFocus() {
    this.#isFocused.set(true);
    this.#rawInput.set(this.#element.value);
  }
  #setValue(value: T | null) {
    const parsedValue = this.adapterType()?.parse(value);
    this.value.set(parsedValue);
  }
  onInput(): void {
    const value = this.#element.value;
    this.#rawInput.set(value);
    this.#setValue(value as T);
  }
  onKeydown(e: KeyboardEvent) {
    this.adapterType().keydownHandler(e);
  }
}
